import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { SiteChange } from './siteChange';

/**
 * "Start over from <new site>?" — rebuilding from a different website replaces the Brand OS, so it is confirmed
 * first. Cancel has the focus when the dialog opens (Radix AlertDialog), so Enter never starts over by accident.
 */
export function StartOverDialog({ change, busy, onCancel, onConfirm }: {
  change: SiteChange | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (change: SiteChange) => void;
}) {
  return (
    <AlertDialog open={!!change} onOpenChange={(open) => { if (!open && !busy) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Start over from {change?.newSite}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-[13px] text-[#475467]">
              <p>
                This Brand OS was built from <strong className="text-[#101828]">{change?.previousSite}</strong>. Starting
                over replaces its identity, products (promotables), audiences, competitors and the market map, and market
                briefs with what's found on <strong className="text-[#101828]">{change?.newSite}</strong>.
              </p>
              <p>Library files and the brand wiki stay as they are.</p>
              <p className="text-[#b42318]" style={{ fontWeight: 600 }}>This can't be undone.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(e) => { e.preventDefault(); if (change) onConfirm(change); }}
            className="bg-[#d92d20] text-white hover:bg-[#b42318]"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Start over
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
